import {
	type AnyFrontendStage,
	type FrontendPlacementMessage,
} from '@post-office/message-maker-browser/types';
import { noValidation } from '@post-office/message-maker-browser/message-helpers';
import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';
import { PlacementFrontend } from '@post-office/browser--growth/confluence-side-nav';
import { SideNavBanner } from '../../../ui/SideNavBanner';
import { PromoModal } from '../../../ui/PromoModal';
import { StorageClient } from '@atlaskit/frontend-utilities/StorageClient';
import { useCallback, useEffect, useMemo, useState } from 'react';

// Created on first use: constructing a StorageClient on the server defines a global `window`.
let storageClient: StorageClient | undefined;
const getStorageClient = (): StorageClient =>
	(storageClient ??= new StorageClient('confluence-recent-downgrade-discount-modal', {
		storageEngine: 'localStorage',
	}));
const SEEN_KEY = 'seen';

const getSeenValue = (): boolean => getStorageClient().getItem(SEEN_KEY) === true;
const setSeenValue = (): void => getStorageClient().setItemWithExpiry(SEEN_KEY, true);

type MessageFrontendProps = {
	entitlementId: string;
	placeOrderHref: string;
	edition: string;
	productName: string;
	shouldForceOpenModal: boolean;
	handleOnDismissed: () => void;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<MessageFrontendProps>(),
		Component: ({
			productName,
			edition,
			entitlementId,
			placeOrderHref,
			shouldForceOpenModal,
			handleOnDismissed,
		}: MessageFrontendProps) => {
			const [isModalOpen, setIsModalOpen] = useState(() => shouldForceOpenModal && !getSeenValue());

			const analyticsAttributes = useMemo(
				() => ({
					productName,
					edition,
					entitlementId,
				}),
				[productName, edition, entitlementId],
			);

			useEffect(() => {
				if (isModalOpen && !getSeenValue()) {
					setSeenValue();
				}
			}, [isModalOpen]);

			const handleBannerClick = useCallback(() => {
				setIsModalOpen(true);
			}, []);

			const handleModalClose = useCallback(() => {
				setSeenValue();
				setIsModalOpen(false);
			}, []);

			const handleDismissBanner = useCallback(() => {
				setSeenValue();
				handleOnDismissed();
			}, [handleOnDismissed]);

			return (
				<>
					<SideNavBanner
						onClick={handleBannerClick}
						onDismiss={handleDismissBanner}
						productName={productName}
						edition={edition}
						bannerName="confluenceRecentDowngradeDiscountBanner"
						analyticsAttributes={analyticsAttributes}
					/>
					<PromoModal
						isOpen={isModalOpen}
						onClose={handleModalClose}
						productName={productName}
						edition={edition}
						placeOrderHref={placeOrderHref}
						modalName={'confluenceRecentDowngradeDiscountModal'}
					/>
				</>
			);
		},
	},
});
